<template>
	<InlineModal
		variant="small"
		:open="open"
		@update:open="handleUpdateOpen"
	>
		<template #header>
			<div class="mb-1 flex w-full items-center justify-end gap-3">
				<button
					type="button"
					@click="$emit('update:open', false)"
				>
					<XMarkIcon class="text-secondary-300! h-6 w-6" />
				</button>
			</div>
		</template>
		<template v-if="showFuneralDirectorSignatureModal">
			<div class="flex flex-col items-center justify-center gap-6 text-center">
				<section>
					<h4 class="text-secondary-900! mb-1 font-bold">
						{{ t('document.missingFuneralDirectorSignatureHeader') }}
					</h4>
					<p class="px-8 font-normal text-slate-500!">
						{{ t('document.missingFuneralDirectorSignatureMessage') }}
					</p>
				</section>
				<section class="flex w-full flex-col gap-4">
					<button
						type="button"
						class="bg-accent-400! text-accent-700! hover:bg-accent-600! hover:text-accent-900! h-12 w-full rounded-lg font-bold"
						@click="handleConfirmFuneralDirectorSignature"
					>
						{{ t('document.missingFuneralDirectorSignatureConfirm') }}
					</button>
					<button
						type="button"
						class="text-accent-500! hover:bg-accent-500! hover:border-accent-500! border-accent-400! h-12 w-full rounded-lg border-3 font-bold hover:text-white!"
						@click="handleCancelFuneralDirectorSignature"
					>
						{{ t('document.missingFuneralDirectorSignatureClose') }}
					</button>
				</section>
			</div>
		</template>
		<!--show all missing required items-->
		<template v-else>
			<div class="flex flex-col items-center justify-center gap-4 text-center">
				<section>
					<h4 class="text-secondary-900! mb-1 font-bold">
						{{ t('document.missingItemsHeader') }}
					</h4>
					<p class="px-8 text-sm font-extralight text-slate-500!">
						{{ t('document.missingItemsMessage') }}
					</p>
				</section>
				<section>
					<h4 class="mb-1 text-sm font-bold text-slate-500! underline">
						{{ t('document.missingItemsSubHeader') }}:
					</h4>
					<ul>
						<li
							v-if="!validatedSignatures"
							class="text-sm font-extralight text-slate-700!"
						>
							{{ t('document.missingSignaturesItem') }}
						</li>
						<li
							v-if="!validatedFuneralDirectorSignatures"
							class="text-sm font-extralight text-slate-700!"
						>
							{{ t('document.missingFuneralDirectorSignature') }}
						</li>
						<li
							v-for="unfilledField of unfilledRequiredFields"
							class="text-sm font-extralight text-slate-700!"
						>
							{{ unfilledField }}
						</li>
					</ul>
				</section>
				<section class="flex w-full flex-col gap-4">
					<button
						type="button"
						class="font-bold text-red-500! underline"
						@click="handleFinalize"
					>
						{{ t('document.finalizeDocument') }}
					</button>
					<button
						type="button"
						class="bg-accent-400! text-accent-700! hover:bg-accent-500! hover:text-accent-800! mb-2 w-full rounded-lg py-2 font-bold"
						@click="$emit('update:open', false)"
					>
						{{ t('document.cancelFinalize') }}
					</button>
				</section>
			</div>
		</template>
	</InlineModal>
</template>

<script setup lang="ts">
import InlineModal from '@/components/common/InlineModal.vue'
import { usePresentationLocale } from '@/i18n'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { XMarkIcon } from '@heroicons/vue/24/solid'
import { computed, ref } from 'vue'

const { t } = usePresentationLocale()
const sessionDocumentsStore = useSessionDocumentsStore()
const emit = defineEmits(['update:open', 'finalize'])

const props = defineProps<{
	unfilledRequiredFields: string[]
	validatedSignatures: boolean
	validatedFuneralDirectorSignatures: boolean
	open: boolean
}>()

const forceShowFuneralDirectorSignatureModal = ref(false)

const stateRequiresFDSignature = computed(() => {
	return sessionDocumentsStore.isStateRequiresSignature(
		sessionDocumentsStore.currentPlanHolderID
	)
})

// Only show the funeral director signature modal if:
// - The funeral director signatures are not validated
// - All other signatures are validated
// - All required fields are filled
// - The session is in a funeral director signature state (e.g. TX, OH, PA, and WV)
const showFuneralDirectorSignatureModal = computed(() => {
	if (
		!stateRequiresFDSignature.value ||
		props.validatedFuneralDirectorSignatures
	) {
		return false
	}

	if (forceShowFuneralDirectorSignatureModal.value) {
		return true
	}

	const onlyMissingFDSignatures =
		props.validatedSignatures && props.unfilledRequiredFields.length === 0

	return onlyMissingFDSignatures
})

const handleFinalize = () => {
	if (
		!props.validatedFuneralDirectorSignatures &&
		stateRequiresFDSignature.value
	) {
		forceShowFuneralDirectorSignatureModal.value = true
		return
	}

	emit('finalize')
}

const handleConfirmFuneralDirectorSignature = () => {
	forceShowFuneralDirectorSignatureModal.value = false
	emit('finalize')
}

const handleCancelFuneralDirectorSignature = () => {
	forceShowFuneralDirectorSignatureModal.value = false
	emit('update:open', false)
}

const handleUpdateOpen = (open: boolean) => {
	if (!open) {
		forceShowFuneralDirectorSignatureModal.value = false
	}

	emit('update:open', open)
}
</script>
